/* ==========================================================================
   Sprayy — pre-register form

   Posts to Klaviyo's client subscriptions endpoint:
     POST https://a.klaviyo.com/client/subscriptions?company_id=<public key>

   The public API key is public by design — it can only create subscriptions,
   it cannot read anything out of the account. Never put a private key here.

   Request shape below was verified against the live API on the 2025-07-15
   revision; a valid call returns 202 with an empty body.
   ========================================================================== */

(function () {
  'use strict';

  var ENDPOINT = 'https://a.klaviyo.com/client/subscriptions';
  var REVISION = '2025-07-15';

  var MESSAGES = {
    firstName: 'Pop your first name in.',
    lastName: 'And your last name.',
    email: 'That email doesn’t look right — mind checking it?',
    phone: 'That mobile doesn’t look right. Try 0412 345 678.',
    consent: 'Tick the box so we know it’s okay to email you.',
    config: 'This form isn’t connected yet. (No Klaviyo list ID set.)',
    network: 'Couldn’t reach our end — check your connection and try again.',
    generic: 'Something went wrong on our side. Try again in a moment.'
  };

  /**
   * Normalise an Australian mobile to E.164, which is what Klaviyo requires.
   * Accepts 0412345678, 412345678, +61412345678, and spaced variants.
   * Returns null when it can't produce something plausible.
   */
  function normalisePhone(raw) {
    if (!raw) return null;

    var value = String(raw).trim();
    var hasPlus = value.charAt(0) === '+';
    var digits = value.replace(/\D/g, '');

    if (!digits) return null;

    // Already international, and not an AU number — pass it through as typed.
    if (hasPlus && digits.indexOf('61') !== 0) {
      return digits.length >= 8 && digits.length <= 15 ? '+' + digits : null;
    }

    if (digits.indexOf('61') === 0) digits = digits.slice(2);
    else if (digits.charAt(0) === '0') digits = digits.slice(1);

    // AU mobiles are 4xx xxx xxx once the trunk 0 is gone.
    if (!/^4\d{8}$/.test(digits)) return null;

    return '+61' + digits;
  }

  function isEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value).trim());
  }

  function buildPayload(fields, listId, source) {
    var nowIso = new Date().toISOString();

    var profile = {
      email: fields.email,
      first_name: fields.firstName,
      last_name: fields.lastName,
      subscriptions: {
        email: {
          marketing: {
            consent: 'SUBSCRIBED',
            consented_at: nowIso
          }
        }
      }
    };

    // Only claim SMS consent when they actually gave us a number.
    // Klaviyo requires consented_at for SMS opt-ins under TCPA/AU consent rules.
    if (fields.phone) {
      profile.phone_number = fields.phone;
      profile.subscriptions.sms = {
        marketing: {
          consent: 'SUBSCRIBED',
          consented_at: nowIso
        },
        transactional: {
          consent: 'SUBSCRIBED',
          consented_at: nowIso
        }
      };
    }

    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: source || 'Pre-register page',
          profile: { data: { type: 'profile', attributes: profile } }
        },
        relationships: {
          list: { data: { type: 'list', id: listId } }
        }
      }
    };
  }

  function init(form) {
    var companyId = form.dataset.companyId;
    var listId = form.dataset.listId;
    var source = form.dataset.source;

    var button = form.querySelector('[data-prereg-submit]');
    var errorEl = form.querySelector('[data-prereg-error]');
    var root = form.closest('.sprayy-prereg');
    var formPanel = root.querySelector('[data-prereg-panel="form"]');
    var successPanel = root.querySelector('[data-prereg-panel="success"]');

    var firstNameEl = form.querySelector('[name="first_name"]');
    var lastNameEl = form.querySelector('[name="last_name"]');
    var emailEl = form.querySelector('[name="email"]');
    var phoneEl = form.querySelector('[name="phone"]');

    var submitting = false;

    function showError(message, focusEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
      if (focusEl) focusEl.focus();
    }

    function clearError() {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    function setLoading(state) {
      submitting = state;
      button.disabled = state;
      button.classList.toggle('sprayy-prereg__button--loading', state);
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (submitting) return;
      clearError();

      var fields = {
        firstName: firstNameEl.value.trim(),
        lastName: lastNameEl.value.trim(),
        email: emailEl.value.trim(),
        phone: null
      };

      if (!fields.firstName) return showError(MESSAGES.firstName, firstNameEl);
      if (!fields.lastName) return showError(MESSAGES.lastName, lastNameEl);
      if (!isEmail(fields.email)) return showError(MESSAGES.email, emailEl);

      if (phoneEl && phoneEl.value.trim()) {
        fields.phone = normalisePhone(phoneEl.value);
        if (!fields.phone) return showError(MESSAGES.phone, phoneEl);
      }

      if (!listId) {
        showError(MESSAGES.config);
        return;
      }

      setLoading(true);

      fetch(ENDPOINT + '?company_id=' + encodeURIComponent(companyId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: REVISION
        },
        body: JSON.stringify(buildPayload(fields, listId, source))
      })
        .then(function (response) {
          if (response.status === 202 || response.ok) {
            formPanel.hidden = true;
            successPanel.hidden = false;
            successPanel.focus();

            if (window.Shopify && window.Shopify.analytics) {
              try {
                window.Shopify.analytics.publish('sprayy_prereg_signup', {
                  has_phone: Boolean(fields.phone)
                });
              } catch (e) {
                /* analytics is best-effort */
              }
            }
            return;
          }

          return response
            .json()
            .catch(function () {
              return null;
            })
            .then(function (body) {
              var detail =
                body && body.errors && body.errors[0] && body.errors[0].detail;

              // Surface the real reason in the console for whoever's debugging,
              // but keep the on-screen copy human.
              if (detail) console.warn('[Sprayy pre-register] Klaviyo:', detail);

              if (detail && /list/i.test(detail)) {
                showError(MESSAGES.config);
              } else if (detail && /email/i.test(detail)) {
                showError(MESSAGES.email, emailEl);
              } else if (detail && /phone/i.test(detail)) {
                showError(MESSAGES.phone, phoneEl);
              } else {
                showError(MESSAGES.generic);
              }
              setLoading(false);
            });
        })
        .catch(function (error) {
          console.warn('[Sprayy pre-register]', error);
          showError(MESSAGES.network);
          setLoading(false);
        });
    });

    [firstNameEl, lastNameEl, emailEl, phoneEl].forEach(function (el) {
      if (el) el.addEventListener('input', clearError);
    });
  }

  function boot() {
    document.querySelectorAll('[data-prereg-form]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-bind when the section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    var form = event.target.querySelector('[data-prereg-form]');
    if (form) init(form);
  });
})();
